import React, { useState } from 'react';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';

export function InfoIcon({ tooltip }) {
  const [isOpen, setIsOpen] = useState(false);
  let closeTimeout;

  React.useEffect(() => {
    return () => {
      clearTimeout(closeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!tooltip || (typeof tooltip === 'string' && tooltip.trim() === "")) {
    return null;
  }

  const handleMouseEnter = () => {
    clearTimeout(closeTimeout);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeout = setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  return (
    <Popover className="relative inline-flex align-middle">
      <>
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <PopoverButton
            as="span"
            className="ml-1.5 text-xs text-muted-foreground/70 cursor-help focus:outline-none leading-none hover:text-muted-foreground transition-colors"
            aria-describedby="tooltip-panel"
          >
            &#9432;
          </PopoverButton>
        </div>

        <Transition
          show={isOpen}
          as={React.Fragment}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <PopoverPanel
            id="tooltip-panel"
            static
            anchor="bottom end"
            className="z-[100] mt-2 w-72 rounded-lg border border-border/60 bg-background-elevated p-3.5 text-sm font-normal text-foreground shadow-lg ring-1 ring-black/5 focus:outline-none whitespace-normal break-words leading-relaxed"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {tooltip}
          </PopoverPanel>
        </Transition>
      </>
    </Popover>
  );
}

export function CheckboxInput({ id, label, tooltip, checked, onChange }) {
  return (
    <div className="flex items-center">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 text-accent border-border/60 rounded transition-colors focus:ring-2 focus:ring-accent/20 focus:ring-offset-2"
      />
      <label htmlFor={id} className="ml-2.5 text-sm text-foreground cursor-pointer">
        {label} <InfoIcon tooltip={tooltip} />
      </label>
    </div>
  );
}

export function NumberInput({ id, label, tooltip, value, onChange, min, max, step }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-muted-foreground mb-2">
        {label} <InfoIcon tooltip={tooltip} />
      </label>
      <input
        id={id}
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={onChange}
        className="w-full bg-background-soft border border-border/60 rounded-lg px-3.5 py-2.5 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all duration-200"
      />
    </div>
  );
}

export function RangeInput({ id, label, tooltip, value, onChange, min = 0, max = 1, step = 0.01 }) {
  return (
    <div className="col-span-2">
      <label className="block text-sm font-medium text-muted-foreground mb-2.5">
        {label} <InfoIcon tooltip={tooltip} />
      </label>
      <div className="flex items-center space-x-4">
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={onChange}
          className="w-full h-2 bg-background-soft rounded-lg appearance-none cursor-pointer accent-accent [&::-webkit-slider-thumb]:bg-accent [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:shadow-sm [&::-moz-range-thumb]:bg-accent [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4"
        />
        <span className="text-sm text-foreground font-medium tabular-nums min-w-[3rem] text-right">
          {(value * 100).toFixed(0)}%
        </span>
      </div>
    </div>
  );
}

export function SelectInput({ id, label, tooltip, value, options, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-muted-foreground mb-2">
        {label} <InfoIcon tooltip={tooltip} />
      </label>
      <select
        id={id}
        value={value}
        onChange={onChange}
        className="w-full bg-background-soft border border-border/60 rounded-lg px-3.5 py-2.5 text-foreground focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all duration-200 cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

